import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Webhook, Database, RefreshCw, ArrowRight, Code, Headphones, Zap, Shield } from 'lucide-react';
import PageHeader from '../components/PageHeader';

export default function IntegrationsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            Enterprise Healthcare Integrations
          </h1>
          <p className="mt-5 text-xl text-gray-500">
            Seamlessly connect your 4D EMR system with leading CRM platforms
          </p>
        </div>

        {/* Premier Partner */}
        <div className="mb-20">
          <Card className="p-8 bg-gradient-to-br from-indigo-50 to-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium mb-4">
                  Premier CRM Partner
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">ZoneDM Integration</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Experience seamless patient data synchronization with our premier CRM partner. ZoneDM offers specialized healthcare CRM features designed to work perfectly with 4D EMR.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-center text-gray-700">
                    <Zap className="w-5 h-5 text-indigo-600 mr-3" />
                    Real-time patient data synchronization
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Shield className="w-5 h-5 text-indigo-600 mr-3" />
                    HIPAA-compliant data transfer
                  </li>
                  <li className="flex items-center text-gray-700">
                    <RefreshCw className="w-5 h-5 text-indigo-600 mr-3" />
                    Automated appointment management
                  </li>
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80"
                  alt="Healthcare Dashboard"
                  className="rounded-lg shadow-xl"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Integration Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <Card className="p-6">
            <Code className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Full API Access</h3>
            <p className="text-gray-600">
              Comprehensive REST API with detailed documentation for custom integrations and workflows.
            </p>
          </Card>

          <Card className="p-6">
            <Headphones className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Integration Support</h3>
            <p className="text-gray-600">
              Dedicated technical support team to assist with implementation and optimization.
            </p>
          </Card>

          <Card className="p-6">
            <Shield className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">HIPAA Compliance</h3>
            <p className="text-gray-600">
              Enterprise-grade security with end-to-end encryption for all integrations.
            </p>
          </Card>
        </div>

        {/* Popular Integrations */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Coming Soon
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Salesforce', description: 'Enterprise CRM integration' },
              { name: 'HubSpot', description: 'Marketing automation integration' },
              { name: 'Custom CRM', description: 'Build your own integration' }
            ].map((integration) => (
              <Card key={integration.name} className="p-6 text-center">
                <h3 className="text-lg font-semibold text-gray-900">{integration.name}</h3>
                <p className="mt-2 text-sm text-gray-500">{integration.description}</p>
                <button className="mt-4 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-not-allowed opacity-60">
                  Coming Soon
                </button>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-xl shadow-xl overflow-hidden">
          <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Ready to integrate your EMR?
            </h2>
            <p className="mt-4 text-lg leading-6 text-indigo-100">
              Our team is here to help you get started with your integration needs.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}